/**
 * briefingCirculation.js — Briefing spécifique "Séance de circulation 3h" du permis AM.
 * Contexte : dernière étape obligatoire de la formation AM (8h). Élève 14-16 ans sur scooter 50 cm³,
 * moniteur qui suit derrière (moto/scooter + radio intercom). Objectif : validation de la formation.
 * 7 branches + 5 réflexes + mini QCM d'ancrage.
 */

export const briefing = {
  title: 'Briefing Séance Circulation',
  subtitle: 'Ma séance de 3h en circulation · Dernière étape du permis AM',
  mantra: 'ÉCOUTER · REGARDER · SE POSITIONNER · ADAPTER · AGIR',
  slogan: 'Je vais valider ma formation — je suis prêt.',
  posterUrl: 'https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/2dflxfh6_ChatGPT%20Image%2021%20ao%C3%BBt%202026%2C%2007_44_25.png',

  /* -------- 7 branches -------- */
  branches: [
    {
      num: 1,
      color: 'blue',
      title: 'Aujourd\'hui, comment ça se passe ?',
      subtitle: '3h de circulation · déroulé',
      icon: 'Route',
      points: [
        { icon: 'MapPin', text: 'Départ auto-école · itinéraire choisi par le moniteur' },
        { icon: 'Route', text: 'Trajet varié : lotissement, agglo, hors agglo, giratoires' },
        { icon: 'Radio', text: 'Le moniteur me suit derrière en scooter/moto avec radio' },
        { icon: 'Users', text: 'Arrêts réguliers pour débrief à chaud (5 à 10 min)' },
      ],
      consigne: "Ce n'est PAS un examen — c'est le moment où tu valides ce que tu as appris en 5 h précédentes. Ton moniteur est là pour t'accompagner, pas te piéger.",
    },
    {
      num: 2,
      color: 'emerald',
      title: 'Le check départ',
      subtitle: 'Avant de sortir du parking',
      icon: 'Shield',
      lookZones: [
        'Casque bien attaché (jugulaire à 2 doigts)',
        "Gants + veste manches longues + chaussures fermées",
        'Pneus gonflés + freins qui répondent',
        'Feux, clignotants, klaxon opérationnels',
        "Attestation AM/BSR + assurance sur moi",
      ],
      method: ['CASQUE', 'TENUE', 'SCOOTER', 'PAPIERS'],
      consigne: "Je fais mon check devant le moniteur — c'est le premier geste du pilote autonome.",
    },
    {
      num: 3,
      color: 'orange',
      title: 'Ce qui est évalué aujourd\'hui',
      subtitle: 'Les 6 points que le moniteur observe',
      icon: 'Eye',
      subCards: [
        {
          title: '✓ Points positifs recherchés',
          tone: 'ok',
          bullets: [
            'Regard porté LOIN devant, pas sur la roue',
            'Positionnement adapté (agglo / hors agglo)',
            'Respect du code (stops, feux, priorités)',
            'Trajectoires propres en virage et giratoire',
            "Utilisation des clignotants à chaque changement",
            'Distance de sécurité maintenue',
          ],
        },
        {
          title: '✗ Points à éviter',
          tone: 'warn',
          bullets: [
            'Regard fixe sur le sol ou le tableau de bord',
            'Rester scotché contre le trottoir en agglo',
            'Oublier de contrôler ses rétros avant un changement',
            'Couper un virage sans visibilité',
            'Suivre trop près la voiture devant',
          ],
        },
      ],
      consigneForte: 'AUCUNE FAUTE N\'EST DÉFINITIVE : le moniteur reprend, on corrige, on continue.',
    },
    {
      num: 4,
      color: 'purple',
      title: 'Les consignes radio du moniteur',
      subtitle: 'Ce que tu vas entendre dans le casque',
      icon: 'Radio',
      keyPhrase: "La radio, c'est ton coach en temps réel. Je réponds par un signe de tête ou de main.",
      allows: ['CORRIGER SUR L\'INSTANT', 'ANTICIPER LE PROCHAIN GESTE', 'RESTER SEREIN'],
      adapts: [
        '« Regarde loin » → je remonte mon regard',
        '« Décale-toi » → je change de position dans ma voie',
        '« Ralentis » → je lâche l\'accélérateur, je freine progressif',
        '« Clignotant » → je le mets AVANT de manœuvrer',
        '« Prochain à droite » → je prépare mon changement de direction',
      ],
      consigne: "Si je n'ai pas compris, je lève la main en fin d'action et on en parle au prochain arrêt.",
    },
    {
      num: 5,
      color: 'sky',
      title: 'Les gestes prof · réflexes',
      subtitle: 'Les 4 situations qui reviennent le plus',
      icon: 'CornerDownRight',
      phases: [
        {
          title: 'Approche d\'intersection',
          steps: ['Je ralentis franchement', 'Je regarde les 2 côtés', "Je respecte le stop/priorité", 'Je repars uniquement si c\'est libre'],
        },
        {
          title: 'Entrée de giratoire',
          steps: ['Clignotant si sortie != tout droit', 'Je cède la priorité aux véhicules à gauche', 'Je choisis ma voie', 'Regard vers la sortie'],
        },
        {
          title: 'Dépassement par une voiture',
          steps: ['Je vérifie mon rétro gauche', 'Je me décale légèrement à droite', 'Je maintiens mon allure', "J'attends qu'il ait dégagé"],
        },
        {
          title: 'Freinage d\'urgence',
          steps: ['Frein AVANT en priorité (2 doigts)', 'Frein arrière en appoint', 'Regard loin (pas sur l\'obstacle)', 'Je maintiens la moto droite'],
        },
      ],
      consigne: "Un scooter de 50, ça freine court MAIS ça glisse vite en virage. Je ralentis TOUJOURS AVANT le virage.",
    },
    {
      num: 6,
      color: 'lime',
      title: 'Les arrêts débrief',
      subtitle: 'Toutes les 20-30 min avec le moniteur',
      icon: 'Users',
      roles: [
        {
          role: 'Ce que le moniteur va me dire',
          icon: 'Crown',
          motto: 'Ce qui a marché · ce qui doit s\'améliorer',
          bullets: [
            'Il commence toujours par ce qui a été bon',
            'Il me montre 1 ou 2 points concrets à corriger',
            'Il me donne l\'objectif du prochain tronçon',
            'Il répond à mes questions',
          ],
        },
        {
          role: 'Ce que je peux (dois) demander',
          icon: 'Bike',
          motto: 'Poser des questions = signe de maturité',
          bullets: [
            '« Est-ce que ma position était bonne dans le virage ? »',
            '« J\'ai hésité à ce giratoire, comment mieux faire ? »',
            '« Est-ce que je regarde assez loin ? »',
            '« Qu\'est-ce que je dois travailler en priorité ? »',
          ],
        },
      ],
      regle: 'Un élève qui pose des questions est un élève qui progresse. Je ne dois JAMAIS rester avec un doute.',
    },
    {
      num: 7,
      color: 'red',
      title: 'Si un imprévu arrive',
      subtitle: 'Panne · chute · perte du moniteur',
      icon: 'AlertTriangle',
      communication: {
        motto: 'Rester calme · me signaler · attendre',
        signals: [
          "Panne mécanique : je me range, je descends, gilet jaune, j'attends le moniteur",
          "Chute sans mal : je vérifie mon corps AVANT le scooter, je ne bouge pas si douleur",
          "Je perds le moniteur de vue : je m'arrête au prochain endroit sûr et j'attends",
          "Je ne comprends pas une consigne : je lève la main, on s'arrête à la prochaine occasion",
        ],
      },
      perte: {
        title: 'MA PRIORITÉ ABSOLUE',
        headline: 'La sécurité passe TOUJOURS avant la performance de la séance.',
        steps: ['Je me range en sécurité', "J'attends", "Je respire", "Je repars quand je suis prêt"],
      },
    },
  ],

  /* -------- 5 réflexes AM circulation -------- */
  reflexes: [
    { num: 1, title: 'J\'écoute la radio', desc: 'Le moniteur me guide en temps réel.', icon: 'Radio', color: 'blue' },
    { num: 2, title: 'Je regarde loin', desc: 'Jamais sur ma roue ni mon compteur.', icon: 'Eye', color: 'emerald' },
    { num: 3, title: "J'anticipe", desc: 'Je clignote AVANT, je ralentis AVANT.', icon: 'Gauge', color: 'orange' },
    { num: 4, title: 'Je respecte le Code', desc: "Stops, feux, priorités, distances — tout compte.", icon: 'Shield', color: 'red' },
    { num: 5, title: 'Je pose des questions', desc: "Aux arrêts, je clarifie ce que je n'ai pas compris.", icon: 'Brain', color: 'purple' },
  ],
};

/* -------- Mini QCM d'ancrage · 5 réflexes séance circulation AM -------- */
export const briefingQuiz = {
  id: 'briefing-circulation',
  title: 'QCM Briefing · Séance Circulation AM',
  questions: [
    {
      q: "Pendant ma séance de 3h de circulation, mon moniteur est :",
      choices: [
        "Devant moi pour que je le suive",
        "Derrière moi en scooter/moto avec une radio pour me guider",
        "Dans une voiture qui me suit",
        "Au bureau, il attend mon retour",
      ],
      answer: 1,
      explain: "Le moniteur me suit DERRIÈRE en 2-roues avec une radio-intercom. Il voit ce que je fais et me guide en direct dans le casque.",
    },
    {
      q: "En agglomération, où dois-je me placer sur ma voie ?",
      choices: [
        "Le plus à droite possible, contre le trottoir",
        "Entre les 2 files de voitures pour aller plus vite",
        "Au centre ou 1/3 gauche, à l'écart des portières",
        "Peu importe, personne ne regarde",
      ],
      answer: 2,
      explain: "En agglo : centre ou 1/3 gauche. J'évite les portières qui s'ouvrent et je gagne en visibilité dans les rétros des voitures. JAMAIS entre 2 files.",
    },
    {
      q: "Une voiture veut me doubler sur une départementale. Je fais quoi ?",
      choices: [
        "J'accélère à fond pour la battre",
        "Je me décale légèrement à droite pour lui faciliter le dépassement",
        "Je reste au milieu de la voie pour l'empêcher",
        "Je freine brutalement",
      ],
      answer: 1,
      explain: "Mon scooter fait 45 km/h max. Je facilite le dépassement en me décalant à droite dans MA voie. Je vérifie mon rétro et je maintiens mon allure.",
    },
    {
      q: "À l'arrêt débrief, le moniteur me montre un point à corriger. Je :",
      choices: [
        "Me vexe et je râle",
        "Écoute, je pose des questions si je n'ai pas compris, je le note pour le prochain tronçon",
        "Fais semblant d'avoir compris",
        "Change d'auto-école",
      ],
      answer: 1,
      explain: "Un arrêt débrief, c'est un moment clé pour progresser. Je pose des questions, je clarifie, je ne repars JAMAIS avec un doute. Personne ne me juge.",
    },
    {
      q: "Dans un virage SANS visibilité, la règle absolue est :",
      choices: [
        "Je fonce pour être fixé",
        "Je coupe le virage pour raccourcir",
        "Je ralentis AVANT, je ne coupe pas, je reste capable de m'arrêter",
        "Je suis la trajectoire du véhicule devant",
      ],
      answer: 2,
      explain: "Je ne coupe JAMAIS un virage sans visibilité. Je ralentis AVANT d'entrer et je reste capable de m'arrêter sur la distance que je vois. C'est LE réflexe qui sauve.",
    },
  ],
};

export default briefing;
